import { useLocation, useSearchParams } from "react-router-dom";
import { beginProfile } from "../utils/renderProfiler";
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Combine,
  LineChart as LineChartIcon,
  Users,
  Car,
  Activity,
  Map,
  KeyRound,
} from "lucide-react";
import SendCommandModal from "./SendCommandModal";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../store/authSlice";
import { downloadEntityTemplate, uploadEntityFile } from "../services/importExportService";

// --- Custom Hooks and Components Imports ---
import useAdminData from "./admin/useAdminData";
import useAdminHandlers from "./admin/useAdminHandlers";
import StaticsDashboard from "./admin/StaticsDashboard";
import UserManagement, { EditUserModal, CreateUserHubModal } from "./admin/UserManagement";
import DeviceManagement from "./admin/DeviceManagement";
import AssetManagement from "./admin/AssetManagement";
import EventLogs from "./admin/EventLogs";
import MapTracker from "./admin/MapTracker";
import ResetPasswordModal from "./admin/ResetPasswordModal";
import AddDeviceModal from "./admin/AddDeviceModal";
import AddAssetModal from "./admin/AddAssetModal";
import EditAssetModal from "./admin/EditAssetModal";
import GeofenceModal from "./admin/GeofenceModal";
import "./admin/admindashboard.css";
import { useGetAlarmsQuery } from "../redux/admin/alarmApi";
import RegisterDeviceModal from "./admin/RegisterDeviceModal";

// 🚀 RBAC IMPORTS
import {
  hasPermission,
  hasUserPermission,
  ROLES,
  getManageableRoles,
  getCreatableRoles,
} from "../utils/rbacHelper";
import QuickActions from "./admin/QuickActions";
import TaskManagement from "./employee/TaskManagement";
import MyTasks from "./employee/MyTasks";
import { useStatus } from "../popup/NotificationContext";
import CreateCustomerModal from "./modals/CreateCustomerModal";
import CreateDealerModal from "./modals/CreateDealerModal";
import SensorAssignModal from "./modals/SensorAssignModal";
import AlarmDashboard from "../popup/AlarmDashboard";
import AlarmPopupSystem from "../popup/AlarmPopupSystem";
import CameraControlPortal from "./cameraV2/CameraControlPortal";
import {
  buildAdminDashboardPath,
  buildLiveMapPath,
  consumeDashboardNavPayload,
  LIVE_MAP_PATH,
  normalizeDashboardTab,
  readDashboardTabFromSearch,
} from "../router/dashboardNavigation";
import { useDevRenderInstrumentation } from "../utils/devInstrumentation";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const normalizeRole = (role) => {
  const r = String(role || "").trim().toLowerCase();
  if (r === "superadmin" || r === "super_admin") return ROLES.SUPER_ADMIN;
  if (r === "groupadmin" || r === "dealer") return ROLES.DEALER;
  if (r === "subadmin") return ROLES.SUB_ADMIN;
  if (r === "officeemployee") return ROLES.OFFICE_EMPLOYEE;
  if (r === "fieldemployee") return ROLES.FIELD_EMPLOYEE;
  return r;
};

const toIdString = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") return String(value._id || value.id || "");
  return String(value);
};

function AdminDashboard({
  activeTab: propActiveTab,
  setActiveTab: propSetActiveTab,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.auth.user);
  const { showStatus } = useStatus();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // --- 1. Refs & Identifiers ---
  const audioRef = useRef(null);
  const prevEventsRef = useRef();
  const didFetchRef = useRef(false);

  const finishAdminDashboardProfile = beginProfile("AdminDashboard", { tab: propActiveTab || "internal" });
  const profileSnapshotRef = useRef(null);

  const currentUserRole = normalizeRole(currentUser?.role);
  const currentUserId = currentUser?._id;
  const isViewer = currentUserRole === ROLES.VIEWER;

  // --- 2. Local States ---
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [deviceAddresses, setDeviceAddresses] = useState({});
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [alarms, setAlarms] = useState([]);
  const [pendingQuickAction, setPendingQuickAction] = useState(null);
  const [pendingFocusRoute, setPendingFocusRoute] = useState(null);
  const [pendingPlaybackFocus, setPendingPlaybackFocus] = useState(null);
  const [pendingMapStatusFilter, setPendingMapStatusFilter] = useState("all");

  const [internalActiveTab, setInternalActiveTab] = useState(() => {
    try {
      const routeTab = readDashboardTabFromSearch(window.location.search, "");
      if (routeTab) return routeTab;
      const savedTab = sessionStorage.getItem("adminDashboardActiveTab");
      if (savedTab === "assignDevice" || savedTab === "assignUserToGA")
        return "statics";
      if (currentUserRole === ROLES.VIEWER) return "map";
      if (currentUserRole === ROLES.FIELD_EMPLOYEE) return "my_tasks";
      return savedTab || "statics";
    } catch (err) {
      return "statics";
    }
  });

// Use prop activeTab if provided, otherwise use internal state
const routeActiveTab = readDashboardTabFromSearch(location.search, internalActiveTab);
const activeTab = propActiveTab || routeActiveTab || internalActiveTab;
const isMapRoute =
  location.pathname === LIVE_MAP_PATH ||
  (location.pathname === "/dashboard" &&
    new URLSearchParams(location.search).get("tab") === "map");
const setActiveTab = useCallback((nextTab) => {
  const normalized = normalizeDashboardTab(nextTab, activeTab);
  if (propSetActiveTab) {
    propSetActiveTab(normalized);
    return;
  }
  setInternalActiveTab(normalized);
  if (location.pathname === LIVE_MAP_PATH) {
    return;
  }
  setSearchParams((current) => {
    const next = new URLSearchParams(current);
    next.set("tab", normalized);
    return next;
  }, { replace: true });
}, [activeTab, location.pathname, propSetActiveTab, setSearchParams]);

useDevRenderInstrumentation("AdminDashboard", () => ({
  activeTab,
  currentUserId: currentUser?._id || "",
  selectedDevicesCount: selectedDevices.length,
  alarmsCount: alarms.length,
  viewer: isViewer,
}));

console.debug("[MAP ROUTE]", location.pathname, location.search, {
  activeTab,
  isCurrentMapRoute: isMapRoute,
});

useEffect(() => {
  const routeTab = readDashboardTabFromSearch(location.search, internalActiveTab);
  if (routeTab !== internalActiveTab) {
    setInternalActiveTab(routeTab);
  }
}, [internalActiveTab, location.search]);

useEffect(() => {
  if (location.pathname !== "/dashboard") return;
  if (searchParams.get("tab")) return;
  setSearchParams((current) => {
    const next = new URLSearchParams(current);
    next.set("tab", normalizeDashboardTab(activeTab, "statics"));
    return next;
  }, { replace: true });
}, [activeTab, location.pathname, searchParams, setSearchParams]);

useEffect(() => {
  if (!isMapRoute && activeTab === "map") {
    setInternalActiveTab("statics");
  }
}, [isMapRoute, activeTab, location.pathname, location.search]);


  // Update sessionStorage when activeTab changes
  useEffect(() => {
    try {
      if (activeTab !== "map") {
        sessionStorage.setItem("adminDashboardActiveTab", activeTab);
      }
    } catch (e) {
      console.error("Failed to save tab to sessionStorage:", e);
    }
  }, [activeTab]);

  const [routeStart, setRouteStart] = useState(null);
  const [routeEnd, setRouteEnd] = useState(null);
  const [routeDetails, setRouteDetails] = useState(null);
  const [locationError, setLocationError] = useState("");
  const [showLocationError, setShowLocationError] = useState(false);
  const [showFilter, setShowFilter] = useState(true);
  const [showCreateCustomerModal, setShowCreateCustomerModal] = useState(false);
  const [showCreateDealerModal, setShowCreateDealerModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetUserId, setResetUserId] = useState(null);
  const [editingAsset, setEditingAsset] = useState(null);
  const [newAssetData, setNewAssetData] = useState({
    IMEI: "",
    serialNumber: "",
    model: "",
    type: "hardwire",
    status: "unassigned",
    simCard: { IMSI: "", MSDN: "", provider: "", expiryDate: "" },
    purchaseDate: "",
    notes: "",
  });

  const [showAddDeviceModal, setShowAddDeviceModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showAddAssetModal, setShowAddAssetModal] = useState(false);
  const [showEditAssetModal, setShowEditAssetModal] = useState(false);
  const [showGeofenceModal, setShowGeofenceModal] = useState(false);
  const [selectedDeviceForGeofence, setSelectedDeviceForGeofence] =
    useState(null);
  const [unsavedGeofence, setUnsavedGeofence] = useState(null);
  const [geofenceName, setGeofenceName] = useState("");
  const [editingDevice, setEditingDevice] = useState({ assignedToUser: null });
  const [showEditDeviceModal, setShowEditDeviceModal] = useState(false);

  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [deviceSearchQuery, setDeviceSearchQuery] = useState("");
  const [assignUserSearchQuery, setAssignUserSearchQuery] = useState("");
  const [groupAdminSearchQuery, setGroupAdminSearchQuery] = useState("");
  const [assignDeviceId, setAssignDeviceId] = useState("");
  const [assignToUserId, setAssignToUserId] = useState("");
  const [showRegisterDeviceModal, setShowRegisterDeviceModal] = useState(false);

  const [assignUserToGroupAdminUserId, setAssignUserToGroupAdminUserId] =
    useState("");
  const [assignUserToGroupAdminTargetId, setAssignUserToGroupAdminTargetId] =
    useState("");

  // State for Sensor Modal
  const [showSensorModal, setShowSensorModal] = useState(false);
  const [selectedDeviceForSensor, setSelectedDeviceForSensor] = useState(null);

  // Handler
  const handleSensorManagement = useCallback((device) => {
    setSelectedDeviceForSensor(device);
    setShowSensorModal(true);
  }, []);

  // --- 3. Authorization Logic ---
  const handleLogout = useCallback(() => dispatch(logout()), [dispatch]);
  const can = useCallback(
    (action) => hasUserPermission(currentUser, action),
    [currentUser],
  );
  const setMessageWithTimeout = useCallback((setter, msg) => {
    setter(msg);
    setTimeout(() => setter(""), 3000);
  }, []);

  const rolesToFetch = [
    "admin",
    "manager",
    "hr",
    "field_emp",
    "office_emp",
    "sub_admin",
    "group_admin",
    "client",
    "user",
    "viewer",
  ];
  const skipAdminDataFetch = currentUserRole
    ? !rolesToFetch.includes(currentUserRole)
    : true;

  // --- 4. Custom Hooks ---
  const {
    loading,
    error,
    users,
    devices,
    devicesOnMap,
    deviceCounts,
    totalDeviceCount,
    progressiveLoading,
    deviceEvents,
    eventLoading,
    eventError,
    assetsData,
    isAssetsLoading,
    assetError,
    editingUser,
    setEditingUser,
    fetchAdminData,
    fetchAllAssets,
    updateMapViewport,
  } = useAdminData({
    currentUser,
    handleLogout,
    setMessageWithTimeout,
    setErrorMessage,
    prevEventsRef,
    didFetchRef,
    setDeviceAddresses,
    setSelectedDevices,
    activeTab,
    selectedDevices,
    audioRef,
    skipFetch: skipAdminDataFetch,
  });
  useEffect(() => {
    if (profileSnapshotRef.current) {
      finishAdminDashboardProfile(profileSnapshotRef.current);
    }
  });

  useEffect(() => {
    const statusFromQuery = searchParams.get("status");
    if (statusFromQuery) {
      setPendingMapStatusFilter(String(statusFromQuery));
    }

    const navToken = searchParams.get("nav");
    const navPayload = consumeDashboardNavPayload(navToken);
    const fallbackState = location.state && typeof location.state === "object" ? location.state : null;
    const mergedPayload = {
      ...(navPayload || {}),
      ...(fallbackState || {}),
    };

    if (mergedPayload.quickAction) {
      setPendingQuickAction(mergedPayload.quickAction);
      setActiveTab("map");
    }
    if (mergedPayload.focusRoute) {
      setPendingFocusRoute(mergedPayload.focusRoute);
      setActiveTab("map");
    }
    if (mergedPayload.playbackFocus) {
      setPendingPlaybackFocus(mergedPayload.playbackFocus);
      setActiveTab("map");
    }

    if (!navToken && !fallbackState) return;

    const normalizedTab = normalizeDashboardTab(searchParams.get("tab"), activeTab);
    const nextPath =
      location.pathname === "/live-map"
        ? buildLiveMapPath({
            mapStatusFilter: statusFromQuery,
          })
        : buildAdminDashboardPath({
            tab: normalizedTab,
            mapStatusFilter: statusFromQuery,
          });

    navigate(nextPath, { replace: true });
  }, [activeTab, location.state, navigate, searchParams, setActiveTab]);
  profileSnapshotRef.current = {
    props: {
      activeTab: propActiveTab || internalActiveTab,
      currentUserId,
      currentUserRole,
    },
    state: {
      selectedDevicesCount: selectedDevices.length,
      alarmsCount: alarms.length,
      pendingMapStatusFilter,
    },
    deviceCount: totalDeviceCount || devicesOnMap.length || devices.length,
  };

  const handlers = useAdminHandlers({
    currentUser,
    backendUrl,
    handleLogout,
    setMessageWithTimeout,
    setSuccessMessage,
    setErrorMessage,
    fetchAdminData,
    fetchAllAssets,
    resetUserId,
    newPassword,
    confirmPassword,
    setNewPassword,
    setConfirmPassword,
    setShowResetPasswordModal,
    editingAsset,
    setEditingAsset,
    setShowEditAssetModal,
    newAssetData,
    setNewAssetData,
    setShowAddAssetModal,
    assignDeviceId,
    assignToUserId,
    setAssignDeviceId,
    setAssignToUserId,
    assignUserToGroupAdminUserId,
    assignUserToGroupAdminTargetId,
    setAssignUserToGroupAdminUserId,
    setAssignUserToGroupAdminTargetId,
    editingUser,
    setEditingUser,
    editingDevice,
    setEditingDevice,
    setShowEditDeviceModal,
    selectedDeviceForGeofence,
    setSelectedDeviceForGeofence,
    unsavedGeofence,
    geofenceName,
    setShowGeofenceModal,
    setUnsavedGeofence,
    setGeofenceName,
    setRouteStart,
    setRouteEnd,
    setRouteDetails,
    setLocationError,
    setShowLocationError,
    devices,
  });

  const {
    handleOpenCommandModal,
    handleCloseCommandModal,
    handleIMEIValidation,
    handleCancelValidation,
    handleResetPassword,
    handleAssignDevice,
    handleAssignUserToGroupAdmin,
    handleRemoveManagedUser,
    handleEditUser,
    handleDeleteUser,
    handleRestoreUser,
    handleDeleteDevice,
    handleAddDevice,
    handleAddAsset,
    handleEditAsset,
    handleDeleteAsset,
    handlePermanentDeleteAsset,
    handleRestoreAsset,
    handleDownloadTemplate,
    handleBulkFileChange,
    handleBulkUpload,
    handleExportToExcel,
    handleGeofenceManagement,
    onGeofenceCreated,
    onGeofenceEdited,
    onGeofenceDeleted,
    handleSaveGeofence,
    getDirections,
    formatTimestamp12h,
    handleCreateUser,
    getVehicleIcon,
    vehicleTypeIcons,
    newDeviceModel,
    setNewDeviceModel,
    newDeviceName,
    setNewDeviceName,
    newDeviceIMEI,
    setNewDeviceIMEI,
    newDeviceMSDN,
    setNewDeviceMSDN,
    newDeviceIMSI,
    setNewDeviceIMSI,
    newDeviceType,
    setNewDeviceType,
    newUsername,
    setNewUsername,
    newEmail,
    setNewEmail,
    newMobile,
    setNewMobile,
    newPassword: newModalPassword,
    setNewPassword: setNewModalPassword,
    newUserRole,
    setNewUserRole,
    bulkFile,
    uploading,
    showCommandModal: commandModalVisible,
    deviceForCommand: commandDevice,
    commandModalOptions,
    isIMEIValid: isIMEIValidHandler,
    handleLoginAsUser,
  } = handlers;

  useEffect(() => {
    if (isViewer && isMapRoute && activeTab !== "map") {
      setActiveTab("map");
    }
  }, [isViewer, isMapRoute, activeTab, setActiveTab]);

  useEffect(() => {
    try {
      if (activeTab !== "map") {
        sessionStorage.setItem("adminDashboardActiveTab", activeTab);
      }
    } catch (e) {
      console.error("Failed to save:", e);
    }
  }, [activeTab]);

  // --- 5. Effects ---
  useEffect(() => {
    if (
      error &&
      !error.toString().includes("authorized") &&
      error.toString().trim().toLowerCase() !== "system sync failed"
    )
      showStatus("error", `Sync failed: ${error}`);
  }, [error, showStatus]);

  useEffect(() => {
    if (!showGeofenceModal || !selectedDeviceForGeofence) return;
    const deviceGeofence = selectedDeviceForGeofence.geofences?.[0];
    if (!deviceGeofence) {
      setUnsavedGeofence(null);
      setGeofenceName("");
      return;
    }
    if (deviceGeofence.type === "circle") {
      setUnsavedGeofence({
        type: "circle",
        data: {
          center: {
            lat: Number(deviceGeofence.center.lat),
            lng: Number(deviceGeofence.center.lng),
          },
          radius: Number(deviceGeofence.radius),
        },
      });
      setGeofenceName(deviceGeofence.name);
    }
    if (deviceGeofence.type === "polygon") {
      const coords = deviceGeofence.polygon?.coordinates?.[0]?.map(
        ([lng, lat]) => ({ lat, lng }),
      );
      if (coords?.length) {
        setUnsavedGeofence({ type: "polygon", data: coords });
        setGeofenceName(deviceGeofence.name);
      }
    }
  }, [
    showGeofenceModal,
    selectedDeviceForGeofence,
    setUnsavedGeofence,
    setGeofenceName,
  ]);

  // --- 6. Early Returns ---
  if (!currentUser)
    return <div className="ad-loading-screen">Authenticating...</div>;
  if (loading && !skipAdminDataFetch)
    return (
      <div className="ad-loading-screen">
        <div className="ad-spinner"></div>Loading Dashboard...
      </div>
    );

  const manageableRoles = getManageableRoles(currentUserRole);
  const creatableRoles = getCreatableRoles(currentUserRole);

  // ✅ CRITICAL FIX: Ensure arrays are always defined with fallback
  const safeUsers = Array.isArray(users) ? users : [];
  const safeDevices = Array.isArray(devices) ? devices : [];

  const scopedUsers = safeUsers.filter((user) => {
    const role = normalizeRole(user.role);
    const userId = toIdString(user?._id);
    const createdBy = toIdString(user?.createdBy);
    const dealerOwner = toIdString(user?.dealerOwner);
    const managedUserIds = (currentUser?.managedUsers || []).map((managedUser) =>
      toIdString(managedUser?._id || managedUser)
    );

    if (currentUserRole === ROLES.SUPER_ADMIN) return role !== ROLES.SUPER_ADMIN;
    if ([ROLES.MANAGER, ROLES.OFFICE_EMPLOYEE, ROLES.HR].includes(currentUserRole)) {
      return manageableRoles.includes(role);
    }

    return (
      userId === currentUserId ||
      manageableRoles.includes(role) &&
        (createdBy === currentUserId ||
          dealerOwner === currentUserId ||
          managedUserIds.includes(userId))
    );
  });

  const assignableUsersForDevices = scopedUsers.filter((user) =>
    manageableRoles.includes(normalizeRole(user.role))
  );

  // ✅ ROLE-BASED USER FILTERING
  const assignableUsersForGroupAdmin = safeUsers.filter((user) => {
    if (
      currentUserRole === ROLES.SUPER_ADMIN ||
      currentUserRole === ROLES.MANAGER
    ) {
      return (
        normalizeRole(user.role) === ROLES.USER ||
        normalizeRole(user.role) === ROLES.CLIENT ||
        normalizeRole(user.role) === ROLES.VIEWER
      );
    }
    if (currentUserRole === ROLES.SUB_ADMIN) {
      return (
        normalizeRole(user.role) === ROLES.USER ||
        normalizeRole(user.role) === ROLES.CLIENT ||
        normalizeRole(user.role) === ROLES.VIEWER
      );
    }
    if (currentUserRole === ROLES.DEALER) {
      return (
        normalizeRole(user.role) === ROLES.USER ||
        normalizeRole(user.role) === ROLES.CLIENT ||
        normalizeRole(user.role) === ROLES.VIEWER
      );
    }
    return false;
  });

  // ✅ ROLE-BASED ADMIN FILTERING
  const groupAdmins = safeUsers.filter((user) => {
    if (
      currentUserRole === ROLES.SUPER_ADMIN ||
      currentUserRole === ROLES.MANAGER
    ) {
      return normalizeRole(user.role) === ROLES.SUB_ADMIN || normalizeRole(user.role) === ROLES.DEALER;
    }
    if (currentUserRole === ROLES.OFFICE_EMPLOYEE) {
      return normalizeRole(user.role) === ROLES.SUB_ADMIN;
    }
    if (currentUserRole === ROLES.SUB_ADMIN) {
      return normalizeRole(user.role) === ROLES.DEALER;
    }
    return false;
  });

  // const { data: alarmsData = [], isLoading: alarmsLoading } = useGetAlarmsQuery({
  //   status: 'active',
  //   limit: 50
  // });

  const assignmentLabels = {
    userLabel: "Select User",
    adminLabel:
      currentUserRole === ROLES.OFFICE_EMPLOYEE
        ? "Select Sub-Admin"
        : currentUserRole === ROLES.SUB_ADMIN
          ? "Select Dealer"
          : "Select Admin/Dealer",
    adminPlaceholder:
      currentUserRole === ROLES.OFFICE_EMPLOYEE
        ? "Sub-Admin Name"
        : currentUserRole === ROLES.SUB_ADMIN
          ? "Dealer Name"
          : "Sub-Admin or Dealer Name",
  };

  return (
    <div className={`ad-wrapper ${isViewer ? "viewer-mode" : ""} no-sidebar`}>
      <AlarmPopupSystem />
      <audio
        ref={audioRef}
        src="/assets/event.wav"
        muted
        autoPlay
        preload="auto"
      ></audio>

      <div className="ad-main-content full-width">
        <div className="ad-tab-container">
          {activeTab === "statics" && can("viewDashboard") && (
            <StaticsDashboard deviceCounts={deviceCounts} />
          )}

          {activeTab === "map" && (
           <MapTracker
  devicesOnMap={devicesOnMap.length ? devicesOnMap : safeDevices}
  sidebarDevices={safeDevices}
              deviceEvents={deviceEvents}
              selectedDevices={selectedDevices}
              setSelectedDevices={setSelectedDevices}
              deviceAddresses={deviceAddresses}
              getDirections={getDirections}
              formatTimestamp12h={formatTimestamp12h}
              handleOpenCommandModal={
                can("sendCommands") ? handleOpenCommandModal : null
              }
              routeStart={routeStart}
              routeEnd={routeEnd}
              routeDetails={routeDetails}
              setRouteDetails={setRouteDetails}
              showFilter={showFilter}
              setShowFilter={setShowFilter}
              locationError={locationError}
              showLocationError={showLocationError}
              setShowLocationError={setShowLocationError}
              deviceCounts={deviceCounts}
              totalDeviceCount={totalDeviceCount}
              vehicleTypeIcons={vehicleTypeIcons}
              getVehicleIcon={getVehicleIcon}
              viewerMode={isViewer}
              currentUserRole={currentUserRole}
              quickAction={pendingQuickAction}
              clearQuickAction={() => setPendingQuickAction(null)}
              externalRouteFocus={pendingFocusRoute}
              clearExternalRouteFocus={() => setPendingFocusRoute(null)}
              externalPlaybackFocus={pendingPlaybackFocus}
              clearExternalPlaybackFocus={() => setPendingPlaybackFocus(null)}
              initialSidebarStatus={pendingMapStatusFilter}
              progressiveLoading={progressiveLoading}
              onMapViewportChange={updateMapViewport}
            />
          )}

          {activeTab === "users" && can("viewUsers") && (
            <UserManagement
              users={scopedUsers}
              creatableRoles={creatableRoles}
              manageableRoles={manageableRoles}
              currentUser={currentUser}
              currentUserId={currentUserId}
              currentUserRole={currentUserRole}
              setEditingUser={setEditingUser}
              handleDeleteUser={handleDeleteUser}
              handleRestoreUser={handleRestoreUser}
              handleRemoveManagedUser={handleRemoveManagedUser}
              setResetUserId={setResetUserId}
              setShowResetPasswordModal={setShowResetPasswordModal}
              handleLoginAsUser={handleLoginAsUser}
              setShowCreateUserModal={setShowCreateUserModal}
              canEdit={can("editUser")}
              canDelete={can("deleteUser")}
              canCreate={can("createUser")}
              canAssignDevice={can("createDevice")}
              canAssignUserToGA={can("assignUserToGA")}
              assignmentLabels={assignmentLabels}
              handleAssignDevice={handleAssignDevice}
              handleAssignUserToGroupAdmin={handleAssignUserToGroupAdmin}
              safeDevices={safeDevices}
              assignableUsersForDevices={assignableUsersForDevices}
              assignableUsersForGroupAdmin={assignableUsersForGroupAdmin}
              groupAdmins={groupAdmins}
              deviceSearchQuery={deviceSearchQuery}
              setDeviceSearchQuery={setDeviceSearchQuery}
              assignDeviceId={assignDeviceId}
              setAssignDeviceId={setAssignDeviceId}
              userSearchQuery={userSearchQuery}
              setUserSearchQuery={setUserSearchQuery}
              assignToUserId={assignToUserId}
              setAssignToUserId={setAssignToUserId}
              assignUserSearchQuery={assignUserSearchQuery}
              setAssignUserSearchQuery={setAssignUserSearchQuery}
              assignUserToGroupAdminUserId={assignUserToGroupAdminUserId}
              setAssignUserToGroupAdminUserId={setAssignUserToGroupAdminUserId}
              groupAdminSearchQuery={groupAdminSearchQuery}
              setGroupAdminSearchQuery={setGroupAdminSearchQuery}
              assignUserToGroupAdminTargetId={assignUserToGroupAdminTargetId}
              setAssignUserToGroupAdminTargetId={setAssignUserToGroupAdminTargetId}
            />
          )}

          {activeTab === "devices" && can("viewDevices") && (
            <DeviceManagement
              devices={safeDevices}
              setShowRegisterDeviceModal={setShowRegisterDeviceModal}
              assignableUsersForDevices={assignableUsersForDevices}
              formatTimestamp12h={formatTimestamp12h}
              setEditingDevice={setEditingDevice}
              setShowEditDeviceModal={setShowEditDeviceModal}
              handleGeofenceManagement={handleGeofenceManagement}
              handleDeleteDevice={handleDeleteDevice}
              currentUserRole={currentUserRole}
              setShowAddDeviceModal={setShowAddDeviceModal}
              canEdit={can("editDevice")}
              canDelete={can("deleteDevice")}
              geofenceAlarms={
                deviceEvents?.filter((e) => e.type === "geofence") || []
              }
              currentUser={currentUser}
              handleSensorManagement={handleSensorManagement}
            />
          )}
          <RegisterDeviceModal
            open={showRegisterDeviceModal}
            onClose={() => setShowRegisterDeviceModal(false)}
            onCreated={() => fetchAdminData()}
          />

          {activeTab === "assignments" && can("viewDashboard") && (
            <div className="ad-grid-layout">
              <div className="ad-grid-right">
                <QuickActions
                  currentUser={currentUser}
                  currentUserRole={currentUserRole}
                  setShowAddDeviceModal={setShowAddDeviceModal}
                  setShowCreateUserModal={setShowCreateUserModal}
                  setShowAddAssetModal={setShowAddAssetModal}
                  setShowCreateCustomerModal={setShowCreateCustomerModal}
                  setShowCreateDealerModal={setShowCreateDealerModal}
                />
              </div>
            </div>
          )}

          {activeTab === "alarms" && can("viewEvents") && (
            <AlarmDashboard
              currentUser={currentUser}
              currentUserRole={currentUserRole}
              initialAlarms={[]}
            />
          )}

          {activeTab === "events" && can("viewEvents") && (
            <EventLogs
              deviceEvents={deviceEvents}
              eventLoading={eventLoading}
              eventError={eventError}
              formatTimestamp12h={formatTimestamp12h}
            />
          )}

          {activeTab === "live_camera" && (
            <CameraControlPortal />
          )}

          {activeTab === "task_management" &&
            ["admin", "manager", "office_emp"].includes(currentUserRole) && (
              <TaskManagement />
            )}
          {activeTab === "my_tasks" && currentUserRole === "field_emp" && (
            <MyTasks />
          )}

          {activeTab === "assignments" && can("viewAssets") && (
            <div className="ad-full-width">
              <AssetManagement
                assetsData={assetsData}
                isAssetsLoading={isAssetsLoading}
                assetError={assetError}
                handleDownloadTemplate={handleDownloadTemplate}
                handleBulkFileChange={handleBulkFileChange}
                handleBulkUpload={handleBulkUpload}
                handleExportToExcel={handleExportToExcel}
                setEditingAsset={setEditingAsset}
                setShowEditAssetModal={setShowEditAssetModal}
                handleRestoreAsset={handleRestoreAsset}
                handlePermanentDeleteAsset={handlePermanentDeleteAsset}
                handleDeleteAsset={handleDeleteAsset}
                bulkFile={bulkFile}
                uploading={uploading}
                currentUserRole={currentUserRole}
                setMessageWithTimeout={setMessageWithTimeout}
                setSuccessMessage={setSuccessMessage}
                setErrorMessage={setErrorMessage}
              />
            </div>
          )}

          {/* --- MODALS --- */}
          {editingUser && (
            <EditUserModal
              editingUser={editingUser}
              setEditingUser={setEditingUser}
              handleEditUser={handleEditUser}
              currentUser={currentUser}
              currentUserId={currentUserId}
              currentUserRole={currentUserRole}
              creatableRoles={creatableRoles}
            />
          )}
          {showAddDeviceModal && (
            <AddDeviceModal
              isIMEIValid={isIMEIValidHandler}
              handleIMEIValidation={handleIMEIValidation}
              handleCancelValidation={handleCancelValidation}
              handleAddDevice={handleAddDevice}
              newDeviceModel={newDeviceModel}
              setNewDeviceModel={setNewDeviceModel}
              newDeviceName={newDeviceName}
              setNewDeviceName={setNewDeviceName}
              newDeviceIMEI={newDeviceIMEI}
              setNewDeviceIMEI={setNewDeviceIMEI}
              newDeviceMSDN={newDeviceMSDN}
              setNewDeviceMSDN={setNewDeviceMSDN}
              newDeviceIMSI={newDeviceIMSI}
              setNewDeviceIMSI={setNewDeviceIMSI}
              newDeviceType={newDeviceType}
              setNewDeviceType={setNewDeviceType}
              setShowAddDeviceModal={setShowAddDeviceModal}
              newDeviceVehicleType={handlers.newDeviceVehicleType}
              setNewDeviceVehicleType={handlers.setNewDeviceVehicleType}
            />
          )}
          {showEditAssetModal && (
            <EditAssetModal
              editingAsset={editingAsset}
              setEditingAsset={setEditingAsset}
              handleEditAsset={handleEditAsset}
              setShowEditAssetModal={setShowEditAssetModal}
            />
          )}
          {showAddAssetModal && (
            <AddAssetModal
              newAssetData={newAssetData}
              setNewAssetData={setNewAssetData}
              handleAddAsset={handleAddAsset}
              setShowAddAssetModal={setShowAddAssetModal}
            />
          )}
          {showCreateUserModal && (
            <CreateUserHubModal
              open={showCreateUserModal}
              onClose={() => setShowCreateUserModal(false)}
              users={scopedUsers}
              creatableRoles={creatableRoles}
              currentUser={currentUser}
              currentUserRole={currentUserRole}
              handleDeleteUser={handleDeleteUser}
              handleRestoreUser={handleRestoreUser}
              handleLoginAsUser={handleLoginAsUser}
              onDownloadUsersTemplate={() => downloadEntityTemplate("users")}
              onUsersFileSelected={async (event) => {
                const file = event?.target?.files?.[0];
                if (!file) return;
                await uploadEntityFile("users", file, { validateOnly: false });
                fetchAdminData?.();
              }}
              canLoginAsUser={can("loginAsUser")}
              setResetUserId={setResetUserId}
              setShowResetPasswordModal={setShowResetPasswordModal}
              onRefresh={() => fetchAdminData?.()}
            />
          )}
          {showGeofenceModal && selectedDeviceForGeofence && (
            <GeofenceModal
              selectedDeviceForGeofence={selectedDeviceForGeofence}
              geofenceName={geofenceName}
              setGeofenceName={setGeofenceName}
              unsavedGeofence={unsavedGeofence}
              setUnsavedGeofence={setUnsavedGeofence}
              onGeofenceCreated={onGeofenceCreated}
              onGeofenceEdited={onGeofenceEdited}
              onGeofenceDeleted={onGeofenceDeleted}
              handleSaveGeofence={handleSaveGeofence}
              setShowGeofenceModal={setShowGeofenceModal}
              successMessage={successMessage}
              errorMessage={errorMessage}
              getVehicleIcon={getVehicleIcon}
              handleDeleteGeofence={handlers.handleDeleteGeofence} // ✅ ADD THIS

            />
          )}
          {showEditDeviceModal && editingDevice && (
            <RegisterDeviceModal
              open={showEditDeviceModal}
              mode="edit"
              initialDevice={editingDevice}
              onClose={() => {
                setShowEditDeviceModal(false);
                setEditingDevice(null);
              }}
              onUpdated={() => {
                setShowEditDeviceModal(false);
                setEditingDevice(null);
                fetchAdminData?.();
              }}
            />
          )}
          {commandModalVisible && (
            <SendCommandModal
              device={commandDevice}
              onClose={handleCloseCommandModal}
              assetsData={assetsData}
              uiMode={commandModalOptions?.uiMode || "simple"}
              commandScope={commandModalOptions?.commandScope || "full"}
              initialCommandType={commandModalOptions?.initialCommandType || ""}
            />
          )}
          {showResetPasswordModal && (
            <ResetPasswordModal
              handleResetPassword={handleResetPassword}
              newPassword={newPassword}
              setNewPassword={setNewPassword}
              confirmPassword={confirmPassword}
              setConfirmPassword={setConfirmPassword}
              setShowResetPasswordModal={setShowResetPasswordModal}
            />
          )}
          {showCreateCustomerModal && (
            <CreateCustomerModal
              handleCreateUser={handleCreateUser}
              setShowCreateCustomerModal={setShowCreateCustomerModal}
              newUsername={newUsername}
              setNewUsername={setNewUsername}
              newEmail={newEmail}
              setNewEmail={setNewEmail}
              newMobile={newMobile}
              setNewMobile={setNewMobile}
              newPassword={newModalPassword}
              setNewPassword={setNewModalPassword}
              currentUserRole={currentUserRole}
            />
          )}
          {showCreateDealerModal && (
            <CreateDealerModal
              handleCreateUser={handleCreateUser}
              setShowCreateDealerModal={setShowCreateDealerModal}
              newUsername={newUsername}
              setNewUsername={setNewUsername}
              newEmail={newEmail}
              setNewEmail={setNewEmail}
              newMobile={newMobile}
              setNewMobile={setNewMobile}
              newPassword={newModalPassword}
              setNewPassword={setNewModalPassword}
              currentUserRole={currentUserRole}
            />
          )}
          {showSensorModal && selectedDeviceForSensor && (
            <SensorAssignModal
              device={selectedDeviceForSensor}
              onClose={() => {
                setShowSensorModal(false);
                setSelectedDeviceForSensor(null);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
